import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import { authApi } from '../../api/auth';
import { handleApiError } from '../../api/client';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('cpw.mismatch'));
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setError(t('cpw.tooShort'));
      return;
    }

    setLoading(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setSuccess(t('cpw.success'));

      // Clear form and close modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('cpw.title')}
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            {t('cpw.cancel')}
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {t('cpw.changeBtn')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        <Input
          type="password"
          label={t('cpw.currentLabel')}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder={t('cpw.currentPh')}
          required
          disabled={loading}
        />

        <Input
          type="password"
          label={t('cpw.newLabel')}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t('cpw.newPh')}
          required
          disabled={loading}
          helperText={t('cpw.newHelp')}
        />

        <Input
          type="password"
          label={t('cpw.confirmLabel')}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t('cpw.confirmPh')}
          required
          disabled={loading}
        />
      </form>
    </Modal>
  );
};

export default ChangePasswordModal;
